// app/src/api/core/utils.ts

import { PaginationType, initialPaginationInfo } from "@/types/common"
import { getStoredWebCsrfToken, isWebSessionAuthMode } from "@core/utils/webSessionAuth"

/**
 * API 응답 처리
 * 서버의 표준 응답 형식을 처리:
 * - 리스트: {'message': 'success', 'results': [...]}
 * - 단일: {'message': 'success', 'result': {...}}
 * - 레거시: {'code': 200, 'message': 'success', 'result': ...}
 */
export const handleApiResponse = <T>(response: any): T => {
  // 새 응답 형식: {'message': 'success', 'results': [...]} 또는 {'message': 'success', 'result': {...}}
  if (response.data && typeof response.data === "object") {
    if (Array.isArray(response.data)) {
      return response.data as T
    }
    // results가 있으면 배열로 반환
    if ("results" in response.data) {
      return (response.data.results ?? []) as T
    }
    // result가 있으면 그것을 반환
    if ("result" in response.data) {
      return response.data.result as T
    }
  }

  // 그 외에는 data 그대로 반환
  return response.data as T
}

export const handleReferenceResponse = (response: any): { columns: string[]; results: any[] } => {
  if (response.data && typeof response.data === "object") {
    return { columns: response.data.columns, results: response.data.results }
  } else {
    return { columns: [], results: [] }
  }
}

/**
 * 페이지네이션이 있는 API 응답 처리
 * DRF 표준 페이지네이션 형식 지원: { count, next, previous, results }
 */
export const handlePageResponse = <T>(response: any): { results: T[]; pagination: PaginationType } => {
  if (response.data && typeof response.data === "object") {
    return {
      results: response.data.results || [],
      pagination: response.data.pagination || initialPaginationInfo,
    }
  } else {
    return { results: [], pagination: initialPaginationInfo }
  }
}

/**
 * URL 쿼리 파라미터 생성 헬퍼
 */
export const buildQueryParams = (params: Record<string, any>): URLSearchParams => {
  const urlParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      urlParams.append(key, value.toString())
    }
  })

  return urlParams
}

/**
 * 인증 헤더 생성
 */
export const getAuthHeaders = () => ({
  ...(isWebSessionAuthMode() && getStoredWebCsrfToken() ? { "X-CSRFToken": getStoredWebCsrfToken() as string } : {}),
})

/**
 * multipart/form-data 헤더 생성
 */
export const getMultipartHeaders = (token?: string) => ({
  "Content-Type": "multipart/form-data",
})
